/**
 * 价格变动弹窗 Hook
 *
 * 功能：
 * 1. 独立监听登录状态变化 - 从未登录状态切换为登录状态
 * 2. 独立监听价格变化 - 由低价格切换到高价格
 * 3. 控制弹窗显示时机 - 避免与其他弹窗冲突，保留弹窗优先级关系
 * 4. 限制显示频率 - 每天只显示一次
 *
 * 重构说明：
 * - 不再依赖领券弹窗和快捷注册弹窗的状态变化来触发
 * - 直接监听登录状态和价格数据变化
 * - 当其他弹窗显示时，价格变动弹窗等待；当其他弹窗关闭后，自动弹出价格变动弹窗
 * - 如果其他弹窗从未显示，延迟后直接显示价格变动弹窗
 */

import { getTimeSign } from '@/api/public';
import { IProductDetail } from '@/api/types/public';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { genPrice } from '@/utils/price';
import { useEffect, useRef } from 'react';

export const usePriceChangeDialog = (
  productInfo: IProductDetail,
  loginProductInfo: IProductDetail,
) => {
  const currency = useStore((state) => state.currency);
  const storeToken = useStore((state) => state.token);
  const quickCouponDialog = useStore((state) => state.quickCouponDialog);
  const surveyGuideDialog = useStore((state) => state.surveyGuideDialog);
  const priceChangeDialog = useStore((state) => state.priceChangeDialog);
  const setPriceChangeDialogShow = useStore((state) => state.setPriceChangeDialogShow);
  const showLoginRegisterDialog = useStore((state) => state.showLoginRegisterDialog);
  const userInfo = useStore((state) => state.userInfo);

  // 存储上一次的登录状态，用于检测登录状态变化
  const prevTokenRef = useRef<string | null>(null);
  // 存储上一次的价格，用于检测价格变化
  const prevPriceRef = useRef<number | null>(null);
  // 标记是否需要显示价格变动弹窗（待显示状态）
  const pendingShowRef = useRef<boolean>(false);
  // 存储上一次的弹窗状态，用于检测弹窗关闭
  const prevDialogStateRef = useRef({
    quickCouponShow: quickCouponDialog.show,
    showLoginRegisterDialog,
    surveyGuideShow: surveyGuideDialog.show,
  });
  // 定时器引用，用于延迟显示弹窗
  const showTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 检查价格变动弹窗是否应该显示（每天只显示一次）
  const checkPriceChangeDialogCondition = async (): Promise<boolean> => {
    try {
      const { code, data = {} } = await getTimeSign({
        type: 'goodsDetailPriceChangeTip',
        second: 1 * 24 * 60 * 60, // 24小时
        value: 1,
      });

      if (code === 200 && data.exist === 0) {
        return true; // 需要弹窗
      }
      return false; // 不需要弹窗
    } catch (error) {
      logger.error('检查价格变动弹窗显示条件失败:', error);
      return false;
    }
  };

  // 获取商品价格
  const getPrice = (item: IProductDetail, _currency: string) => {
    if (
      userInfo.is_member === 1 &&
      item[`price_info_v${userInfo.member_level}` as keyof IProductDetail] &&
      (item[`discount_percent_v${userInfo.member_level}` as keyof IProductDetail] as number) > 0
    ) {
      return genPrice(
        item[`price_info_v${userInfo.member_level}` as keyof IProductDetail] as any,
        _currency,
      );
    }
    if (item.price_info) {
      return genPrice(item.price_info, _currency);
    }
    return { price: 0, sell_price: 0 };
  };

  // 尝试显示价格变动弹窗
  const tryShowPriceChangeDialog = async () => {
    // 如果价格变动弹窗已经在显示，则不重复显示
    if (priceChangeDialog.show) {
      logger.info('价格变动弹窗已在显示，跳过');
      return;
    }

    // 检查其他弹窗是否正在显示
    if (
      prevDialogStateRef.current.quickCouponShow ||
      prevDialogStateRef.current.surveyGuideShow ||
      prevDialogStateRef.current.showLoginRegisterDialog
    ) {
      pendingShowRef.current = true;

      // 设置重试机制：2秒后再次检查
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
      }
      showTimerRef.current = setTimeout(() => {
        logger.info('重试检查弹窗状态');
        tryShowPriceChangeDialog();
      }, 2000);
      return;
    }

    // 检查是否允许显示（每天只显示一次）
    const shouldShow = await checkPriceChangeDialogCondition();
    if (shouldShow) {
      setPriceChangeDialogShow(true);
      pendingShowRef.current = false;
      logger.info('显示价格变动弹窗');
    } else {
      pendingShowRef.current = false;
      logger.info('价格变动弹窗今天已显示过，不再显示');
    }
  };

  // 监听登录状态和价格变化
  useEffect(() => {
    // 如果未登录，重置状态
    if (!storeToken) {
      prevTokenRef.current = null;
      prevPriceRef.current = null;
      pendingShowRef.current = false;
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
      return;
    }

    // 获取未登录时的价格和登录后的价格
    const prevPrice = getPrice(productInfo, currency.name).sell_price;
    const currentPrice = getPrice(loginProductInfo, currency.name).sell_price;

    // 检测价格变化：从低价格切换到高价格
    const priceIncreased =
      prevPrice !== undefined &&
      currentPrice !== undefined &&
      currentPrice > 0 &&
      +prevPrice < +currentPrice;

    // 检查是否应该触发价格变动弹窗
    // 条件：用户已登录 且 价格上涨 且 (刚登录 或 之前记录的价格为0或null)
    const shouldTriggerDialog = storeToken && priceIncreased;

    if (shouldTriggerDialog) {
      logger.info(
        `检测到价格变动弹窗触发条件满足：价格从 ${prevPrice} 上涨到 ${currentPrice}，准备显示价格变动弹窗`,
      );
      pendingShowRef.current = true;

      // 总是延迟显示，给其他弹窗足够时间先显示
      logger.info('价格变动弹窗进入延迟显示队列，延迟3秒后尝试显示');
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
      }
      showTimerRef.current = setTimeout(() => {
        tryShowPriceChangeDialog();
      }, 3000);
    }

    // 更新上一次的状态
    prevTokenRef.current = storeToken;
    prevPriceRef.current = currentPrice;

    // 清理定时器
    return () => {
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
    };
  }, [storeToken, productInfo, loginProductInfo, currency.name]);

  // 监听其他弹窗状态变化，当弹窗关闭时尝试显示价格变动弹窗
  useEffect(() => {
    const prevState = prevDialogStateRef.current;
    const currentQuickCouponShow = quickCouponDialog.show;
    const currentShowLoginRegisterDialog = showLoginRegisterDialog;
    const currentSurveyGuideShow = surveyGuideDialog.show;

    // 检测弹窗从 true 变为 false
    const quickCouponClosed = prevState.quickCouponShow && !currentQuickCouponShow;
    const loginRegisterClosed =
      prevState.showLoginRegisterDialog && !currentShowLoginRegisterDialog;
    const surveyGuideClosed = prevState.surveyGuideShow && !currentSurveyGuideShow;

    // 更新上一次的状态
    prevDialogStateRef.current = {
      quickCouponShow: currentQuickCouponShow,
      showLoginRegisterDialog: currentShowLoginRegisterDialog,
      surveyGuideShow: currentSurveyGuideShow,
    };

    // 如果其他弹窗关闭了，且有待显示的价格变动弹窗，则尝试显示
    if ((quickCouponClosed || loginRegisterClosed || surveyGuideClosed) && pendingShowRef.current) {
      // 延迟一小段时间，确保其他弹窗完全关闭
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
      }
      showTimerRef.current = setTimeout(() => {
        tryShowPriceChangeDialog();
      }, 500);
    }
  }, [quickCouponDialog.show, showLoginRegisterDialog, surveyGuideDialog.show]);
};
